import AppInfoFooter from "@/components/AppInfoFooter"
import Button from "@/components/Button"
import InputErrorMessage from "@/components/InputErrorMessage"
import { PasswordInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Link from "@/components/Link"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import {
  useCheckResetPasswordTokenQuery,
  useResetPasswordMutation,
} from "@/data/queries"
import { backendErrorCodeFor } from "@/model/BackendErrors"
import { VerifyCodePageStateParams } from "@/pages/auth/VerifyCodePage"
import { ROUTES, withQuery } from "@/routes"
import { updatePasswordSchema } from "@/schemas/zod-schemas.ts"
import cn from "@/utils/cn"
import { useSearchParams } from "@/utils/hooks/useSearchParams"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"

type ResetPasswordPageSearchParams = {
  token: string
  entityId: string
}

type ResetPasswordFormData = {
  password: string
  passwordConfirmation: string
}

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams<ResetPasswordPageSearchParams>()
  const { token, entityId: userId } = searchParams

  const { register, handleSubmit, formState } = useForm<ResetPasswordFormData>({
    mode: "all",
    defaultValues: { password: "", passwordConfirmation: "" },
    resolver: zodResolver(updatePasswordSchema),
  })

  const {
    isPending: isValidatingResetPasswordToken,
    error: resetPasswordTokenValidationError,
  } = useCheckResetPasswordTokenQuery({ userId, token })

  const {
    mutateAsync: resetPassword,
    isPending: isResettingPassword,
    error: resetPasswordError,
  } = useResetPasswordMutation()

  const { errors } = formState
  const hasValidResetPasswordToken =
    !isValidatingResetPasswordToken &&
    resetPasswordTokenValidationError === null
  const resetPasswordErrorCode = backendErrorCodeFor(
    resetPasswordError ?? resetPasswordTokenValidationError
  )

  const onSubmit = async (formData: ResetPasswordFormData) => {
    const result = await resetPassword({
      token,
      userId,
      password: formData.password,
    })
    const redirectTo = withQuery(ROUTES.POPULATION.ROOT, {
      protocol: "diabetes",
    })
    if (result.type === "mfa-challenge") {
      const state: VerifyCodePageStateParams = {
        challenge: result.challenge,
        redirectTo,
      }
      navigate(ROUTES.VERIFY_CODE, { state })
      return
    }
    navigate(redirectTo, { replace: true })
  }

  return (
    <Page className="bg-gray-50">
      <div className="flex h-full flex-col justify-between">
        <div
          className={cn(
            "mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl"
          )}
        >
          <h2 className="mb-3 text-center text-3xl font-bold text-primary/90">
            Reset password
          </h2>
          <h3 className="mb-6 text-center text-lg text-primary/90">
            Please create a new password below
          </h3>

          {resetPasswordErrorCode && (
            <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
              <span className="material-icons me-4 text-red-600">error</span>
              <p className="text-red-600">
                {resetPasswordErrorCode === "RESET_PASSWORD_TOKEN_EXPIRED" ? (
                  <>
                    This password reset link has expired.
                    <br />
                    <Link
                      to={ROUTES.LOGIN}
                      replace={true}
                      className="underline"
                    >
                      Go to login
                    </Link>
                  </>
                ) : resetPasswordErrorCode ===
                  "RESET_PASSWORD_ALREADY_EXECUTED" ? (
                  <>
                    The password was already reset via this link. Please&nbsp;
                    <Link
                      to={ROUTES.LOGIN}
                      replace={true}
                      className="underline"
                    >
                      log in
                    </Link>
                    .
                  </>
                ) : resetPasswordErrorCode === "WEAK_PASSWORD" ? (
                  "The password is commonly used and easily guessed. Please choose a stronger password"
                ) : resetPasswordErrorCode === "MFA_SETUP_REQUIRED" ? (
                  "Your account is not set up for two-step verification. Please contact Bloomlife Support."
                ) : (
                  "Something went wrong. Please contact the dev team."
                )}
              </p>
            </div>
          )}

          {isValidatingResetPasswordToken ? (
            <Spinner className="mt-8" />
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-3"
            >
              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="password" className="ml-1">
                  New Password
                </InputLabel>
                <PasswordInput
                  id="password"
                  placeholder="New password"
                  autoComplete="new-password"
                  className="disabled:cursor-not-allowed"
                  disabled={isResettingPassword || !hasValidResetPasswordToken}
                  isError={!!errors?.password?.message}
                  {...register("password")}
                />

                <InputErrorMessage
                  message={
                    <div>
                      {errors?.password?.message && (
                        <p>The Password does not meet the requirements:</p>
                      )}
                      <ul className="ml-4 mt-1.5 list-disc">
                        <li>Minimum 8 characters</li>
                        <li>At least one uppercase letter (A-Z)</li>
                        <li>At least one lowercase letter (a-z)</li>
                        <li>At least one digit (0-9)</li>
                        <li>
                          At least one special character{" "}
                          <span className="tracking-widest">(!@#$%&*?)</span>
                        </li>
                      </ul>
                    </div>
                  }
                  className={cn(
                    "ml-1",
                    errors?.password?.message
                      ? "text-red-600"
                      : "text-primary/90"
                  )}
                />
              </div>

              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="password-confirmation" className="ml-1">
                  Confirm New Password
                </InputLabel>
                <PasswordInput
                  id="password-confirmation"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  className="disabled:cursor-not-allowed"
                  disabled={isResettingPassword || !hasValidResetPasswordToken}
                  isError={!!errors?.passwordConfirmation?.message}
                  {...register("passwordConfirmation")}
                />
                <InputErrorMessage
                  message={errors?.passwordConfirmation?.message}
                  className="ml-1"
                />
              </div>

              <Button
                variant="primary"
                type="submit"
                disabled={isResettingPassword || !hasValidResetPasswordToken}
                className="mt-4 flex w-full gap-3 text-base"
              >
                {isResettingPassword && <Spinner />}
                Set password
              </Button>
            </form>
          )}
        </div>
        <AppInfoFooter className="mt-auto p-4" />
      </div>
    </Page>
  )
}

export default ResetPasswordPage
